import { useMemo } from 'react';
import { subDays, format } from 'date-fns';
import { HabitLog } from '../../types';

export interface CounterTrendDataPoint {
  date: string;
  displayDate: string;
  value: number;       // Actual value logged (rolling average)
  target: number;      // Daily target (constant)
  rate: number;        // value/target ratio (0-1+ scale, rolling average)
  rawValue: number;    // Raw daily value
}

interface RawDayData {
  value: number;
}

const ROLLING_WINDOW = 7;

/**
 * Calculate 7-day rolling average for value.
 */
const calculateRollingAverage = (
  rawData: RawDayData[],
  currentIndex: number
): number => {
  const startIdx = Math.max(0, currentIndex - ROLLING_WINDOW + 1);
  const windowSize = currentIndex - startIdx + 1;
  
  let sum = 0;
  for (let i = startIdx; i <= currentIndex; i++) {
    const day = rawData[i];
    if (!day) continue;
    sum += day.value;
  }
  
  return Math.round((sum / windowSize) * 100) / 100;
};

/**
 * Calculate dynamic date range based on user's app usage history.
 */
const calculateDateRange = (allLogs: HabitLog[]): { startDate: Date; dayCount: number } => {
  if (allLogs.length === 0) {
    return { startDate: subDays(new Date(), 89), dayCount: 90 };
  }
  
  const earliestTimestamp = Math.min(...allLogs.map(l => new Date(l.date).getTime()));
  const earliest = new Date(earliestTimestamp);
  const today = new Date();
  
  const daysSinceStart = Math.floor((today.getTime() - earliest.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysSinceStart < 90) {
    return { startDate: earliest, dayCount: daysSinceStart + 1 };
  }
  
  return { startDate: subDays(today, 89), dayCount: 90 };
};

/**
 * Hook to process Counter habit logs into value vs target trend data.
 * Shows data from app start until 90 days of usage, then switches to rolling 90-day window.
 * 
 * @param logs - All habit logs
 * @param habitId - The specific habit ID to track
 * @param dailyTarget - The daily target value for this counter habit
 * @param requestedDays - Optional override for number of days
 */
export const useCounterTrends = (
  logs: HabitLog[],
  habitId: string,
  dailyTarget: number,
  requestedDays?: number
): CounterTrendDataPoint[] => {
  return useMemo(() => {
    const today = new Date();
    
    // Filter logs for this specific habit
    const habitLogs = logs.filter(l => l.habitId === habitId);
    
    // Calculate dynamic date range
    const { startDate, dayCount } = requestedDays 
      ? { startDate: subDays(today, requestedDays - 1), dayCount: requestedDays }
      : calculateDateRange(logs);
    
    // Group logs by date
    const logsByDate: Record<string, HabitLog[]> = {};
    habitLogs.forEach(log => {
      if (!logsByDate[log.date]) {
        logsByDate[log.date] = [];
      }
      logsByDate[log.date]!.push(log);
    });
    
    // First pass: collect raw daily values
    const rawData: (RawDayData & { dateStr: string; displayDate: string })[] = [];
    
    for (let i = 0; i < dayCount; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const displayDate = format(date, 'MMM d');
      
      const dayLogs = logsByDate[dateStr] || [];
      // Get the value from the log, default to 0 if no log exists
      const value = dayLogs.length > 0 ? (dayLogs[0]!.value || 0) : 0;
      
      rawData.push({
        dateStr,
        displayDate,
        value,
      });
    }
    
    // Second pass: calculate rolling averages
    const dataPoints: CounterTrendDataPoint[] = rawData.map((day, index) => {
      const avgValue = calculateRollingAverage(rawData, index);
      const rate = dailyTarget > 0 ? avgValue / dailyTarget : 0;
      
      return {
        date: day.dateStr,
        displayDate: day.displayDate,
        value: avgValue,
        target: dailyTarget,
        rate,
        rawValue: day.value,
      };
    });
    
    return dataPoints;
  }, [logs, habitId, dailyTarget, requestedDays]);
};

export default useCounterTrends;
